import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from "prop-types";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({children}) => {
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or default to 'auto'
    const savedTheme = localStorage.getItem('scc-theme');
    return savedTheme || 'auto';
  });

  const [resolvedTheme, setResolvedTheme] = useState('light');

  // Function to get OS preference
  const getOSPreference = () => {
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Update resolved theme based on current theme setting
  useEffect(() => {
    let newResolvedTheme;

    if (theme === 'auto') {
      newResolvedTheme = getOSPreference();
    } else {
      newResolvedTheme = theme;
    }

    setResolvedTheme(newResolvedTheme);

    // Apply theme to document
    document.documentElement.dataset.theme = newResolvedTheme;

    // Save to localStorage
    localStorage.setItem('scc-theme', theme);
  }, [theme]);

  // Listen for OS theme changes when in auto mode
  useEffect(() => {
    if (theme !== 'auto') return;

    const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setResolvedTheme(getOSPreference());
      document.documentElement.dataset.theme = getOSPreference();
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    const themes = ['light', 'dark', 'auto'];
    setTheme(prev => themes[(themes.indexOf(prev) + 1) % themes.length]);
  }, []);

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme
  }), [theme, resolvedTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired
};